// Newsletter: Formulare (<fk-newsletter-form>, Shopify-Kundenformular) und Popup (<fk-newsletter-popup>).
// Beim Absenden geht die Adresse zusätzlich an die Storefront-API in Supabase (data-endpoint, keepalive), das
// eigentliche Formular läuft normal über Shopify weiter (Kunde mit Marketing-Einwilligung, ggf. Double-Opt-in).
// Nach dem Absenden merkt sich das Gerät den Stand, damit das Popup nicht erneut erscheint. Das Popup erscheint auf
// jeder Seite nach der eingestellten Zeit (Standard 30 s), einmal je Zeitraum nach dem Schließen.

const KEY = 'fk-newsletter';
const DAY = 24 * 60 * 60 * 1000;
// Analytics events for assets/fk-analytics.js (never the address); a no-op while analytics is off.
const track = (event, properties) => document.dispatchEvent(new CustomEvent('fk:track', { detail: { event, properties } }));

const state = {
  read() {
    try {
      return JSON.parse(localStorage.getItem(KEY) || 'null') || {};
    } catch {
      return {};
    }
  },
  write(patch) {
    try {
      localStorage.setItem(KEY, JSON.stringify({ ...this.read(), ...patch, t: Date.now() }));
    } catch {
      /* kein Speicher */
    }
  },
};

// Shopify hat die Anmeldung angenommen; Double-Opt-in wird erst in der Bestätigungsmail abgeschlossen.
if (new URLSearchParams(location.search).get('customer_posted') === 'true') {
  track('newsletter_signup_accepted', { source: state.read().source });
  state.write({ state: 'submitted' });
}

class FkNewsletterForm extends HTMLElement {
  connectedCallback() {
    const form = this.querySelector('form');
    form?.addEventListener('submit', () => {
      const email = form.querySelector('input[type="email"]')?.value?.trim();
      if (!email) return;
      track('newsletter_signup_submitted', { source: this.dataset.source || 'site' });
      state.write({ state: 'submitted', source: this.dataset.source });
      const endpoint = (this.dataset.endpoint || '').replace(/\/$/, '');
      if (!endpoint) return;
      const legal = this.closest('fk-newsletter-popup, .fk-news, .fk-news-page')?.querySelector('.fk-pop__legal, .fk-news__legal, .fk-news-page__legal')?.textContent?.trim() || '';
      fetch(`${endpoint}/newsletter`, {
        method: 'POST',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          source: this.dataset.source || 'site',
          page: location.pathname,
          locale: document.documentElement.lang || 'en',
          consent_text: legal.slice(0, 500),
        }),
      }).catch(() => {});
    });
  }
}

class FkNewsletterPopup extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog || !this.shouldShow()) return;
    this.querySelector('[data-close]')?.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.dialog.close();
    });
    this.dialog.addEventListener('close', () => {
      if (state.read().state !== 'submitted' && state.read().state !== 'subscribed') {
        state.write({ state: 'dismissed' });
        track('newsletter_popup_dismissed', { seconds_open: Math.round((Date.now() - this.openedAt) / 1000) });
      }
    });
    const delay = (Number(this.dataset.delay) || 8) * 1000;
    this.timer = setTimeout(() => this.tryOpen(0), delay);
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
  }

  shouldShow() {
    if (new URLSearchParams(location.search).get('customer_posted') === 'true') return false;
    if (window.Shopify?.designMode) return false;
    const s = state.read();
    if (s.state === 'submitted' || s.state === 'subscribed') return false;
    const days = Number(this.dataset.days) || 7;
    if (s.state === 'dismissed' && Date.now() - (s.t || 0) < days * DAY) return false;
    return true;
  }

  tryOpen(attempt) {
    if (this.dialog.open) return;
    // Ist gerade ein anderer Dialog offen (Warenkorb, Suche) oder die Altersabfrage noch unbeantwortet, kurz warten –
    // höchstens 60 s, dann trotzdem zeigen. Der Container der Altersabfrage (#age-verification-root) bleibt dauerhaft
    // im DOM; offen ist sie nur, solange er ein sichtbares, großes Kind hat.
    const ageGateOpen = [...document.querySelectorAll('[id*="age-verification"], [class*="age-verification"]')]
      .some((root) => [...root.children].some((c) => c.getBoundingClientRect().height > 100 && getComputedStyle(c).visibility !== 'hidden'));
    if ((document.querySelector('dialog[open]') || ageGateOpen) && attempt < 30) {
      this.timer = setTimeout(() => this.tryOpen(attempt + 1), 2000);
      return;
    }
    this.dialog.showModal();
    this.setAttribute('data-open', '');
    this.openedAt = Date.now();
    track('newsletter_popup_shown', { delay_seconds: (Number(this.dataset.delay) || 8) + attempt * 2 });
    const input = this.dialog.querySelector('input[type="email"]');
    if (input && window.matchMedia('(min-width: 750px)').matches) input.focus();
  }
}

if (!customElements.get('fk-newsletter-form')) customElements.define('fk-newsletter-form', FkNewsletterForm);
if (!customElements.get('fk-newsletter-popup')) customElements.define('fk-newsletter-popup', FkNewsletterPopup);
